import React, { useEffect, useState } from 'react';
import { WrapperContainer } from '../../constants/style/common.styled';
import { COLORS } from '../../constants/style/color';
import Heading from '../../components/typography';
import InitialValuesForm from '../../constants/initialValues';
import { PageHeader } from '../settings/company-profile/profile.styled';
import {
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneNotificationApi,
} from '../../hooks';
import { useNavigate } from 'react-router-dom';
import Spinners from '../../components/spinner';
import EmailTemplateHeader from '../../constants/table-headers/emailTemplateHeader';
import useGetEmailTemplates from '../../hooks/get/useGetEmailTemplates';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import usePostImportNotificationTemplate from '../../hooks/post/usePostImportNotificationTemplate';
import { isEmpty } from 'lodash';
import { useQueryClient } from 'react-query';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import usePostBulkExportNotificationTemplate from '../../hooks/post/usePostBulkExportNotificationTemplate';
import { handleOpenFile } from '../../utils/global';
import { axiosInstance } from '../../services/apiInterceptor';
import PopoverModalComponent from '../../components/modal/popoverModal';
import { useMousePositionAsTrigger } from 'react-laag';
import EmailTemplatePopoverContent from './emailTemplatePopoverContent';

function EmailTemplate() {
  const { handleRequest } = useWealthlaneNotificationApi();
  const { tableInfo, setTableInfo, registerPermissionHandler } = useDynamicTable();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [checkedValues, setCheckedValues] = useState<any>([]);
  const [isTableInfoInitialized, setIsTableInfoInitialized] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);

  const {
    hasMousePosition,
    resetMousePosition,
    handleMouseEvent,
    trigger,
  } = useMousePositionAsTrigger();

  useEffect(() => {
    registerPermissionHandler?.(() => (id: string, event: any, popoverInfo: string) => {
      if (popoverInfo === 'EmailTemplate') {
        setTemplateId(id);
      }
      if (event) {
        handleMouseEvent(event);
      }
    });
  }, [registerPermissionHandler, handleMouseEvent]);

  // Close popover on outside click (same pattern as Roles page)
  useEffect(() => {
    function handleClickOutside(event: any): void {
      if (!(event.target as HTMLElement).className.includes('roles-popovers')) {
        resetMousePosition();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [resetMousePosition]);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'EMAIL_TEMPLATE_LIST_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { mutate: importTemplate } = usePostImportNotificationTemplate(handleRequest, () => {
    queryClient.invalidateQueries('getEmailTemplates');
  });
  const { mutateAsync: exportTemplate } = usePostBulkExportNotificationTemplate(handleRequest);

  const [controls, setControls] = React.useState();
  const [headerList, setHeaderList] = useState<string[]>([]);

  const {
    data: emailTemplates,
    status,
    isLoading: isEmailTemplatesLoading,
  } = useGetEmailTemplates(handleRequest, tableInfo);

  const handleChecked = (values: string[]): void => {
    setCheckedValues(values);
  };

  const handleBulkExport = async (notificationTemplateId: string[]) => {
    const data = await exportTemplate({ notificationTemplateId });
    if (data != null) {
      handleOpenFile(`Export Email Template.xlsx`, data.content);
      setCheckedValues([]);
    }
  };

  const importExcel = (event: any): void => {
    if (!isEmpty(event.target.files)) {
      const payload = {
        File: event.target.files[0],
      };
      importTemplate(payload);
      event.target.value = null;
    }
  };

  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Email Template');

  // Initialize tableInfo with default values
  useEffect(() => {
    if (!isTableInfoInitialized) {
      setTableInfo((prevTableInfo) => ({
        ...InitialValuesForm.tableParams,
        maxResultCount: 1000,
        ...prevTableInfo, // Preserve any existing values
      }));
      setIsTableInfoInitialized(true);
    }
  }, [setTableInfo, isTableInfoInitialized]);

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Email Template',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };
  const tableHeaderList = EmailTemplateHeader({
    actions: schemaData?.columns || [],
    navigate,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const emailTemplateHeader = (): any[] => {
    return EmailTemplateHeader({
      actions: schemaData?.columns || [],
      navigate,
    }).filter((item) => item.isSubHeader === false || headerList.includes(item.accessor));
  };

  const schema = React.useMemo(
    () => ({
      columns: emailTemplateHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  const data = React.useMemo(
    () => emailTemplates?.items || [],
    [emailTemplates, isEmailTemplatesLoading]
  );

  const TemplateDatas = [
    // {
    //   title: 'Download Template',
    //   download: downloadTemplate,
    // },
    {
      title: 'Import Bulk XLS',
      import: importExcel,
    },
  ];

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <>
      <PageHeader className='flex items-end justify-between'>
        {(
          <div>
            <Heading variant='h3' title='Email Templates' />
            <Heading variant='body-s' title={`Email template list.`} color={`${COLORS.Gray}`} />
          </div>
        ) as any}
      </PageHeader>

      <PopoverModalComponent
        hasMousePosition={hasMousePosition}
        trigger={trigger}
        popoverClassName='roles-popovers email-template-popover'
        resetMousePosition={resetMousePosition}
      >
        <EmailTemplatePopoverContent id={templateId} loading={false} />
      </PopoverModalComponent>

      <DynamicTable
        schema={schema}
        templateDatas={TemplateDatas}
        data={data}
        totalCounts={emailTemplates?.totalCount ?? 10}
        loading={status}
        filterControls={controls}
        onChecked={handleChecked}
        checkedValues={checkedValues}
        showCheckBox={true}
        handleExportBulk={() => handleBulkExport(checkedValues)}
        showBulkExport={checkedValues?.length > 0}
        components={{}}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
    </>
  );
}

export default withDynamicTableContext(EmailTemplate, true, axiosInstance);
